"use client";

import {
  endOfDay,
  startOfDay,
  isEqual,
  setHours,
  setMinutes,
  getMinutes,
  getHours,
  isBefore,
  setDay,
  getDay,
  setYear,
  getYear,
  setMonth,
  getMonth,
} from "date-fns";
import { Calendar, CalendarProps } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { LuCalendar, LuX } from "react-icons/lu";
import { DateRange, OnSelectHandler, PropsRange } from "react-day-picker";
import React from "react";
import { defaultRenderType } from "./date-picker";
import { TZDateMini } from "@date-fns/tz";
import { Button, ButtonProps } from "../custom/button";
import { TimePickerRange } from "./time-picker-alt";
import { InputProps } from "./input";
import { DebouncedInput, DebouncedInputProps } from "../custom/debounced-input";
import { Indicator } from "../custom/indicator";
import { useCurrentRef } from "@/hooks/use-current-ref";
import { functionalUpdate } from "@tanstack/react-table";
import { useCssObserver } from "@/hooks/use-css-observer";

type Props = Omit<CalendarProps & PropsRange, "mode">;
export type DateRangePickerProps = Omit<Props, "selected" | "onSelect"> & {
  value?: Props["selected"];
  onChange?: (...event: any[]) => void;
  selected?: Props["selected"];
  onSelect?: Props["onSelect"];
  button?: ButtonProps;
  input?: InputProps;
  isActive?: boolean;
  inline?: boolean;
  placeholder?: string;
  showValue?: boolean;
  clearable?: boolean;
  isDisabled?: boolean;
  onClose?: () => void;
} & {
  renderTime?: (date: Date) => string | undefined;
  parseRenderedTime?: (date: string) => Date | null | undefined;
} & {
  intervals?: number[];
  withAmPm?: boolean;
} & { children?: any };

const isStartOfDay = (date: Date) => isEqual(date, startOfDay(date));
const isEndOfDay = (date: Date) => isEqual(date, endOfDay(date));

const retainStartEnd = (date?: DateRange) => {
  if (!date) return date;
  const next = {
    from: !date.from ? date.from : startOfDay(date.from),
    to: !date.to ? date.to : endOfDay(date.to),
  };
  return next;
};

const chainStartEnd =
  (
    onSelect: OnSelectHandler<DateRange | undefined>,
  ): OnSelectHandler<DateRange | undefined> =>
  (..._args) => {
    const [dateRange, ...args] = _args;
    return onSelect(retainStartEnd(dateRange), ...args);
  };

const retainTime = (date?: Date, selected?: Date) => {
  if (!date || !selected) return date;
  return setHours(setMinutes(date, getMinutes(selected)), getHours(selected));
};

const retainTimeRange = (date?: DateRange, selected?: DateRange) => {
  if (!date || !selected) return date;
  return {
    from: retainTime(date.from, selected.from),
    to: retainTime(date.to, selected.to),
  };
};

const chainRetainTimeRange =
  (selected?: DateRange) =>
  (
    onSelect: OnSelectHandler<DateRange | undefined>,
  ): OnSelectHandler<DateRange | undefined> =>
  (..._args) => {
    const [dateRange, ...args] = _args;
    return onSelect(retainTimeRange(dateRange, selected), ...args);
  };

const emptyChain = (onSelect: OnSelectHandler<DateRange | undefined>) =>
  onSelect;

export const DateRangeInput = ({
  selected,
  onSelect,
  renderTime,
  parseRenderedTime,
  timeZone,
  ...props
}: Pick<DateRangePickerProps, "selected" | "onSelect" | "timeZone"> &
  Required<Pick<DateRangePickerProps, "renderTime" | "parseRenderedTime">> &
  Omit<
    DebouncedInputProps & {
      type: "string";
      isUndefinable: false;
      isNullable: false;
    },
    | "value"
    | "onChange"
    | "selected"
    | "onSelect"
    | "isUndefinable"
    | "isNullable"
    | "type"
  >) => {
  const value = React.useMemo(
    () =>
      selected
        ? `${selected?.from ? renderTime(selected?.from) : ""} - ${selected?.to ? renderTime(selected.to) : ""}`
        : "",
    [selected],
  );
  const [state, setState] = React.useState({
    error: false,
    last: selected,
    pending: undefined as string | undefined,
  });
  React.useMemo(
    () => setState((s) => ({ ...s, error: false, last: selected })),
    [setState, selected],
  );
  const onChange = React.useCallback(
    (value: string) => {
      const [from, to] = value.split("-").map((data) => {
        const trimmed = data.trim();
        const parsed = trimmed
          ? parseRenderedTime(trimmed) || false
          : undefined;
        if (parsed === false) return parsed;
        const date =
          parsed && timeZone ? new TZDateMini(parsed, timeZone) : parsed;
        return date;
      });
      if (from === false || to === false) {
        setState((s) => {
          const last = {
            from: from !== false ? from : s?.last?.from,
            to: to !== false ? to : s?.last?.to,
          };
          return {
            ...s,
            error: true,
            last: last?.from || last?.to ? last : undefined,
          };
        });
      } else {
        const next =
          !from && !to
            ? undefined
            : from && to && isBefore(to, from)
              ? { from: to, to: from }
              : { from: from!, to };
        if (onSelect) {
          onSelect(next, new Date(), {}, undefined as any);
        }
        setState((s) => ({ ...s, error: false, last: next }));
      }
    },
    [parseRenderedTime, timeZone],
  );
  const pendingRef = useCurrentRef(state.pending);
  const onChangeRef = useCurrentRef(onChange);
  React.useEffect(
    () => () => {
      if (pendingRef.current) {
        onChangeRef.current(pendingRef.current);
      }
    },
    [],
  );
  return (
    <>
      <DebouncedInput
        tabIndex={0}
        debounce={-1}
        {...props}
        className={cn(
          "border-0 ring-0 outline-none focus-visible:border-0 focus-visible:ring-0 focus-visible:outline-none shadow-none",
          state.error &&
            "focus-visible:ring-1 ring-1 ring-error focus-visible:ring-error",
          props.className,
        )}
        type={"string"}
        isNullable={false}
        isUndefinable={false}
        value={value}
        onChange={onChange}
        onPending={(pending) => setState((s) => ({ ...s, pending }))}
        onKeyDown={(ev) => {
          if (props.onKeyDown) {
            props.onKeyDown(ev);
          }
          if (state.pending && ev.key == "Enter") {
            ev.preventDefault();
            ev.stopPropagation();
          } else if (onSelect && ev.key === "Escape" && state?.error) {
            ev.preventDefault();
            ev.stopPropagation();
            setState((s) => ({ ...s, error: false }));
            onSelect(state.last, new Date(), {}, ev);
          }
        }}
      />
      {(state.pending !== undefined || state.error) && (
        <span className="ml-auto flex items-center">
          {state.pending !== undefined ? (
            "*"
          ) : state.error ? (
            <Indicator
              variant={"error"}
              onClick={(ev) => {
                if (onSelect) onSelect(state.last, new Date(), {}, ev);
                setState((s) => ({ ...s, error: false }));
              }}
            />
          ) : (
            ""
          )}
        </span>
      )}
    </>
  );
};

const assignDay = (to: Date, from?: Date) => {
  if (!from) return to;
  return setDay(
    setMonth(setYear(to, getYear(from)), getMonth(from)),
    getDay(from),
  );
};

export const DateRangePicker = React.forwardRef<
  HTMLButtonElement,
  DateRangePickerProps
>(
  (
    {
      className,
      selected: __selected,
      onSelect: _onSelect,
      value,
      onChange,
      renderTime = defaultRenderType,
      button,
      input,
      children,
      defaultMonth: _defaultMonth,
      isActive,
      inline,
      showValue = true,
      placeholder,
      clearable,
      intervals,
      withAmPm,
      onClose,
      isDisabled,
      ...props
    },
    ref,
  ) => {
    const _selected = __selected ?? value;
    const selected = React.useMemo(
      () =>
        !_selected || !props.timeZone
          ? _selected
          : {
              from: !_selected?.from
                ? _selected?.from
                : (new TZDateMini(_selected?.from, props.timeZone) as Date),
              to: !_selected?.to
                ? _selected?.to
                : (new TZDateMini(_selected?.to, props.timeZone) as Date),
            },
      [_selected?.from, _selected?.to, props.timeZone],
    );
    const defaultMonth =
      _defaultMonth ?? selected?.from ?? selected?.to ?? (selected as any);
    const onSelect = (_onSelect ?? onChange)!;
    const [open, _setOpen] = React.useState(false);
    const setOpen: typeof _setOpen = React.useCallback(
      (a) =>
        _setOpen((open) => {
          const next = functionalUpdate(a, open);
          if (!next) {
            if (onClose) onClose();
          }
          return next;
        }),

      [onClose, _setOpen],
    );
    const full = React.useMemo(
      () =>
        !selected?.to ||
        (selected?.from &&
          selected.to &&
          isStartOfDay(selected.from) &&
          isEndOfDay(selected.to)),
      [selected],
    );

    const prevFull = React.useRef<boolean | undefined>(full);
    const prevTimeZone = React.useRef<string | undefined>(props.timeZone);
    const prevSelected = React.useRef<DateRange | undefined>(selected);
    React.useEffect(() => {
      if (
        prevTimeZone.current !== props.timeZone &&
        prevFull.current &&
        !full
      ) {
        const nextSelected = selected
          ? {
              from: selected.from
                ? startOfDay(
                    assignDay(selected.from, prevSelected.current?.from),
                  )
                : selected.from,
              to: selected.to
                ? endOfDay(assignDay(selected.to, prevSelected.current?.to))
                : selected.to,
            }
          : undefined;
        onSelect(nextSelected, new Date(), {}, undefined as any);
        if (onClose) {
          onClose();
        }
        prevSelected.current = nextSelected;
      } else {
        prevSelected.current = selected;
      }
      prevFull.current = full;
      prevTimeZone.current = props.timeZone;
      // prevSelected.current = selected;
    }, [full, selected, props.timeZone, onClose]);
    const calendarRef = React.useRef<HTMLDivElement | null>(null);
    const timePickerRef = React.useRef<HTMLDivElement | null>(null);
    useCssObserver({
      target: calendarRef,
      storage: timePickerRef,
      mapper: { "--height": "height" },
    });
    return (
      <Popover
        open={isDisabled ? false : open}
        onOpenChange={isDisabled ? undefined : setOpen}
      >
        <PopoverTrigger disabled={isDisabled} asChild>
          {isDisabled && inline ? (
            <span className={cn("pt-[1px]", className)}>
              {selected?.from || selected?.to
                ? `${selected.from ? renderTime(selected.from) : ""} - ${selected?.to ? renderTime(selected.to) : ""}`
                : (placeholder ?? "<N/A>")}
            </span>
          ) : (
            <Button
              {...button}
              ref={ref}
              variant={button?.variant ?? "none"}
              disabled={button?.disabled ?? isDisabled}
              className={cn(
                "w-full justify-start text-left items-center font-normal border gap-2",
                !selected && "text-muted-foreground",
                inline &&
                  "transition-colors border-none hover:text-foreground hover:bg-ghost",
                inline &&
                  isActive &&
                  "border-azure text-azure hover:text-azure",
                open && "ring-1 ring-azure",
                className,
                button?.className,
              )}
            >
              <LuCalendar className="h-4 w-4" />
              {inline && showValue && (selected?.from || selected?.to) ? (
                <span>{`${selected.from ? renderTime(selected.from) : ""} - ${selected?.to ? renderTime(selected.to) : ""}`}</span>
              ) : inline && placeholder ? (
                <span>{placeholder}</span>
              ) : props.parseRenderedTime && open ? (
                <DateRangeInput
                  {...input}
                  className={cn("h-6", input?.className)}
                  selected={selected}
                  onSelect={onSelect}
                  renderTime={renderTime}
                  timeZone={props.timeZone}
                  parseRenderedTime={props.parseRenderedTime}
                  placeholder={placeholder ?? "Pick a date"}
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                  }}
                  onKeyUp={(ev) => {
                    if (ev.key == "Space") {
                      ev.preventDefault();
                      ev.stopPropagation();
                    }
                  }}
                />
              ) : (
                <span>
                  {selected?.from || selected?.to
                    ? `${selected.from ? renderTime(selected.from) : ""} - ${selected?.to ? renderTime(selected.to) : ""}`
                    : (placeholder ?? "Pick a date")}
                </span>
              )}
              {children}
              {clearable && selected && (
                <LuX
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    onSelect(undefined, new Date(), {}, ev);
                    setOpen(false);
                  }}
                  className={cn(
                    "ml-auto h-3.5 w-3.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-ghost",
                  )}
                />
              )}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="grid grid-cols-[1fr_auto]">
            <div>
              <Calendar
                autoFocus
                {...props}
                selected={selected}
                onSelect={(full
                  ? chainStartEnd
                  : chainRetainTimeRange(selected))(onSelect)}
                defaultMonth={defaultMonth}
                mode="range"
                ref={calendarRef}
              />
            </div>
            {selected && (
              <div className="flex items-center justify-center">
                <div className="flex flex-col" ref={timePickerRef}>
                  <div
                    className="flex items-center shadow w-full px-2 pt-2 pb-1"
                    style={{ clipPath: "inset(0px -12px 0px -12px)" }}
                  >
                    <Button
                      className="w-full"
                      variant={full ? "azureOutline" : "default"}
                      // disabled={full}
                      onClick={(ev) =>
                        chainStartEnd(onSelect)(selected, new Date(), {}, ev)
                      }
                    >
                      {"Full"}
                    </Button>
                  </div>
                  <TimePickerRange
                    className={cn(
                      "h-[calc(var(--height,260px)-1.25rem)]",
                      full && "",
                    )}
                    selected={selected}
                    onSelect={onSelect}
                    timeZone={props.timeZone}
                    withAmPm={withAmPm}
                    intervals={intervals}
                  />
                </div>
              </div>
            )}
          </div>
          {inline && props.parseRenderedTime && selected && (
            <Button
              {...button}
              ref={ref}
              variant={button?.variant ?? "none"}
              className={cn(
                "mx-2 mb-1",
                "w-[calc(100%-1rem)] justify-start text-left items-center font-normal border gap-2",
                "focus-within:ring-azure focus-within:ring-1",
                !selected && "text-muted-foreground",
                // inline &&
                //   isActive &&
                //   "border-azure bg-azure/5 text-azure hover:bg-azure/10",
                // inline && !isActive && "border-aqua hover:bg-aqua/5",
                // open && "ring-1 ring-azure",
                button?.className,
              )}
            >
              <LuCalendar className="h-4 w-4" />
              <DateRangeInput
                {...input}
                className={cn("h-6", input?.className)}
                selected={selected}
                onSelect={onSelect}
                renderTime={renderTime}
                timeZone={props.timeZone}
                parseRenderedTime={props.parseRenderedTime}
                // placeholder={placeholder ?? "Pick a date"}
                placeholder={placeholder ?? "Pick a date"}
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                }}
                onKeyUp={(ev) => {
                  if (ev.key == "Space") {
                    ev.preventDefault();
                    ev.stopPropagation();
                  }
                }}
              />
            </Button>
          )}
        </PopoverContent>
      </Popover>
    );
  },
);
